import { useEffect, useRef, useState, type ReactNode } from "react";
import { api, errorText } from "../api";
import type { SocketStatus } from "../hooks";
import type { State } from "../store";
import { toast } from "../toast";
import type { Stage } from "../types";
import { isPending, upNext } from "./Feed";
import { Brand, Icons } from "./Icons";
import { DensityToggle } from "./DensityToggle";
import { ThemePicker } from "./ThemePicker";

/* The panel, laid out the way a phone is held.

   The desktop panel is a wide grid with a rail, and folding it into one column
   — which is all the stylesheet could ever do — produces a four-thousand-pixel
   scroll whose two time-critical actions, sending a line and killing a clip
   that is already on air, end up at opposite ends of it. Worse, everything you
   navigate with sits in the top 84px, which is the part of a modern phone a
   thumb cannot reach without regripping.

   So on a phone the same pieces are dealt into four screens with a tab bar at
   the bottom, and what is sounding right now rides above it in a docked bar
   that never scrolls away. Nothing is dropped and nothing is rebuilt: every
   slot below is the identical component the desktop shell renders, handed in
   already constructed. That is the whole point of the split — a feature added
   to the panel appears here because it was never possible for it not to. */

type TabId = "speak" | "queue" | "stage" | "more";

interface Props {
  state: State;
  socketStatus: SocketStatus;
  stageName: (stage: Stage) => string;
  onLock: (locked: boolean) => void;
  onSignOut: () => void;
  onOpenSettings: () => void;
  onOpenPresets: () => void;
  onRefresh: () => void;

  banner: ReactNode;
  firstRun: ReactNode;
  composer: ReactNode;
  soundboard: ReactNode;
  queue: ReactNode;
  feed: ReactNode;
  stats: ReactNode;
  /** The admin card, or null — which is also what hides the Stage tab. */
  admin: ReactNode;
}

export function MobileShell({
  state,
  socketStatus,
  stageName,
  onLock,
  onSignOut,
  onOpenSettings,
  onOpenPresets,
  onRefresh,
  banner,
  firstRun,
  composer,
  soundboard,
  queue,
  feed,
  stats,
  admin,
}: Props) {
  const [tab, setTab] = useState<TabId>("speak");
  const [list, setList] = useState<"next" | "history">("next");
  const pane = useRef<HTMLDivElement>(null);
  const isAdmin = state.me?.admin ?? false;
  const queued = state.history.filter(isPending).length;
  const waiting = upNext(
    state.history,
    state.nowPlaying.map((entry) => entry.row.id),
  ).length;

  const tabs: { id: TabId; label: string; icon: ReactNode; badge?: number }[] = [
    { id: "speak", label: "Speak", icon: TabIcons.speak },
    {
      id: "queue",
      label: "Queue",
      icon: TabIcons.queue,
      // The one number worth carrying into the tab bar: it is the reason you
      // would switch tabs at all, and it moves on its own while you are typing.
      badge: queued,
    },
    ...(admin ? [{ id: "stage" as const, label: "Stage", icon: TabIcons.stage }] : []),
    { id: "more", label: "More", icon: TabIcons.more },
  ];

  /* Each tab is its own screen, so each starts at its own top. Without this,
     switching from the bottom of a long history lands you halfway down the
     composer. */
  useEffect(() => {
    pane.current?.scrollTo({ top: 0 });
  }, [tab]);

  return (
    <div className="mobile-shell">
      <header className="mobile-bar">
        <Brand name={state.tenant.display_name} />
        <span className="spacer"></span>

        <span className="pill">
          <span className={`dot ${socketStatus === "live" ? "on" : "off"}`}></span>
          <span>{socketStatus === "live" ? "live" : socketStatus}</span>
        </span>

        {/* The kill switch is the one control that has to be reachable from
            every screen: it is pressed when a stream is going wrong, and
            "which tab was I on" is not a question worth asking then. */}
        {isAdmin ? (
          <button
            className={"lock-toggle" + (state.locked ? " on" : "")}
            aria-pressed={state.locked}
            aria-label={state.locked ? "Let everyone speak again" : "Lock TTS"}
            onClick={() => onLock(!state.locked)}
          >
            <span className={`dot ${state.locked ? "off" : "on"}`}></span>
            {state.locked ? "Locked" : "Lock"}
          </button>
        ) : (
          state.locked && (
            <span className="pill outline" id="lock-pill">
              TTS locked
            </span>
          )
        )}
      </header>

      {banner}

      <div className="tab-pane" ref={pane}>
        {/* Hidden, not unmounted. A half-typed message, a scroll position in
            the history and a slider mid-drag all have to survive a glance at
            another tab — the same reason the admin card's own tabs do it. */}
        <section hidden={tab !== "speak"} className="pane">
          {firstRun}
          {composer}
          {soundboard}
        </section>

        {/* Up next and history as two segments rather than one stacked above
            the other: stacked, every message that arrived pushed the history
            down under the thumb that was reaching for it. What is playing
            lives in the dock, so it is not repeated here. */}
        <section hidden={tab !== "queue"} className="pane">
          <div className="tabs compact list-switch" role="tablist" aria-label="Queue or history">
            <button
              role="tab"
              aria-selected={list === "next"}
              onClick={() => setList("next")}
            >
              Up next{waiting > 0 ? ` (${waiting})` : ""}
            </button>
            <button
              role="tab"
              aria-selected={list === "history"}
              onClick={() => setList("history")}
            >
              History
            </button>
          </div>
          <div hidden={list !== "next"}>{queue}</div>
          <div hidden={list !== "history"}>{feed}</div>
        </section>

        {admin && (
          <section hidden={tab !== "stage"} className="pane">
            {admin}
          </section>
        )}

        <section hidden={tab !== "more"} className="pane">
          {stats}
          <div className="card">
            <h2>This instance</h2>
            <div className="more-actions">
              {isAdmin && (
                <>
                  <button className="more-row" onClick={onOpenSettings}>
                    <span className="more-icon">{Icons.settings}</span>
                    <span className="more-label">
                      Settings

                    </span>
                  </button>
                  <button className="more-row" onClick={onOpenPresets}>
                    <span className="more-icon">{TabIcons.speak}</span>
                    <span className="more-label">
                      Characters
                    </span>
                  </button>
                </>
              )}
              <button className="more-row" onClick={onRefresh}>
                <span className="more-icon">{Icons.refresh}</span>
                <span className="more-label">
                  Refresh voices and credits

                </span>
              </button>
            </div>
          </div>

          <div className="card">
            <h2>You</h2>
            <div className="more-you">
              <b>
                {state.me?.name}
                {isAdmin ? " (admin)" : ""}
              </b>
              <span className="muted">
                {state.online.length} in the panel · {state.overlays} overlay
                {state.overlays === 1 ? "" : "s"} connected
              </span>
            </div>
            <div className="more-theme">
              <span className="field-label">Theme</span>
              <ThemePicker />
              <DensityToggle />
            </div>
            <button className="ghost mobile-signout" onClick={onSignOut}>
              Sign out
            </button>
          </div>
        </section>
      </div>

      <div className="mobile-dock">
        <MiniPlayer
          state={state}
          stageName={stageName}
          admin={isAdmin}
          onExpand={() => setTab("queue")}
        />
        <nav className="tabbar" role="tablist" aria-label="Panel sections">
          {tabs.map((candidate) => (
            <button
              key={candidate.id}
              role="tab"
              aria-selected={tab === candidate.id}
              className={"tab" + (tab === candidate.id ? " on" : "")}
              onClick={() => setTab(candidate.id)}
            >
              <span className="tab-icon">
                {candidate.icon}
                {!!candidate.badge && <span className="tab-badge">{candidate.badge}</span>}
              </span>
              <span className="tab-label">{candidate.label}</span>
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
}

/* What is sounding right now, docked above the tab bar.

   This replaces `.stage`'s sticky positioning, which on a phone was a third
   pinned element competing for a viewport that is only ~600px once the browser
   chrome is up. Same job — the clip on air never scrolls away — done where a
   phone expects to find it, and with the one action that is worth having
   without navigating anywhere: stopping it. */
function MiniPlayer({
  state,
  stageName,
  admin,
  onExpand,
}: {
  state: State;
  stageName: (stage: Stage) => string;
  admin: boolean;
  onExpand: () => void;
}) {
  const playing = state.nowPlaying;

  /* Always there, idle or not. A bar that appeared with the first clip took
     its 48px out of the pane above it, and whatever was at the bottom of the
     screen jumped as the stream talked. */
  if (playing.length === 0) {
    return (
      <div className="miniplayer idle">
        <button className="mini-body" onClick={onExpand} aria-label="Show the queue">
          <span className="wave still" aria-hidden="true">
            <i></i>
            <i></i>
            <i></i>
            <i></i>
          </span>
          <span className="mini-text">
            <span className="mini-msg muted">Nothing on air</span>
          </span>
        </button>
      </div>
    );
  }

  // Several characters can be on air at once, each on its own browser source.
  // The bar shows the newest and says how many more, rather than growing into
  // something that covers the screen.
  const current = playing[playing.length - 1];
  const others = playing.length - 1;

  return (
    <div className="miniplayer">
      <button className="mini-body" onClick={onExpand} aria-label="Show the queue">
        <span className="wave" aria-hidden="true">
          <i></i>
          <i></i>
          <i></i>
          <i></i>
        </span>
        <span className="mini-text">
          <span className="mini-top">
            <b>{current.row.user}</b>
            <span className="muted mono">
              {current.stage === null ? current.row.voice_name : stageName(current.stage)}
            </span>
            {others > 0 && <span className="badge">+{others}</span>}
          </span>
          <span className="mini-msg">{current.row.text}</span>
        </span>
      </button>
      {admin && (
        <button
          className="mini-skip"
          aria-label={`Skip #${current.row.id} on stream`}
          onClick={() =>
            void api(`/api/requests/${current.row.id}/skip`, { method: "POST" })
              .then(() =>
                toast(`Skipped #${current.row.id}`, "", {
                  label: "Undo",
                  run: () =>
                    void api(`/api/requests/${current.row.id}/unskip`, {
                      method: "POST",
                    }).catch((error) => toast(errorText(error), "error")),
                }),
              )
              .catch((error) => toast(errorText(error), "error"))
          }
        >
          {Icons.skip}
        </button>
      )}
    </div>
  );
}

/* Tab bar icons. Bigger and simpler than the row-action set in Icons.tsx: these
   are read at a glance from arm's length, not hovered for a tooltip. */
const TabIcons = {
  speak: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3z" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
    </svg>
  ),
  queue: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 6h11M4 12h11M4 18h7" />
      <path d="m16 14 5 3-5 3z" />
    </svg>
  ),
  stage: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2.5" y="4.5" width="19" height="13" rx="2" />
      <path d="M8 21h8M12 17.5V21" />
    </svg>
  ),
  more: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="5" cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="19" cy="12" r="1.6" />
    </svg>
  ),
} as const;
